import React, { useState } from "react";
import { Phone, CheckCircle2, X } from "lucide-react";
import { toast } from "sonner";
import { ScottyLogo, SCOTTY_LOGO_URL } from "./ScottyLogo";
import {
  quoteServiceOptions,
  PHONE_DISPLAY,
  PHONE_LINK,
} from "../data/business";
import {
  postTrackingEvent,
  buildQuoteTrackingPayload,
  CUSTOM_FIELD_SERVICE,
  CUSTOM_FIELD_DETAILS,
  CUSTOM_FIELD_SERVICE_WANTED,
} from "../lib/form-tracking";

interface QuoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultService?: string;
}

export const QuoteModal: React.FC<QuoteModalProps> = ({
  isOpen,
  onClose,
  defaultService = "",
}) => {
  const fieldId = React.useId();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: defaultService,
    location: "",
    details: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.service) {
      toast.error("Please fill in your name, phone, and select a service.");
      return;
    }

    setIsSubmitting(true);

    const payload = buildQuoteTrackingPayload(
      "quote-modal",
      "Get A Free Quote",
      formData,
    );

    postTrackingEvent(payload, {
      customFields: {
        [CUSTOM_FIELD_SERVICE]: {
          value: formData.service,
          label: "Service Needed",
        },
        [CUSTOM_FIELD_DETAILS]: {
          value: formData.details,
          label: "Project Details",
        },
        [CUSTOM_FIELD_SERVICE_WANTED]: {
          value: formData.service,
          label: "Service Wanted",
        },
      },
    });

    setTimeout(() => {
      setIsSubmitting(false);
      setIsSuccess(true);
      toast.success("Thank you! Your free quote request has been received.");
      setFormData({
        name: "",
        email: "",
        phone: "",
        service: "",
        location: "",
        details: "",
      });
    }, 1000);
  };

  return (
    <div
      className="fixed inset-0 z-[2147483003] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg bg-[#252A2B] text-white rounded-2xl p-4 sm:p-6 border border-white/15 shadow-2xl overflow-hidden max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 text-white/80 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-colors"
          aria-label="Close modal"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="flex flex-col items-center text-center mb-4 pb-3 border-b border-white/10">
          <div className="flex items-center justify-center mb-2">
            <img
              src={SCOTTY_LOGO_URL}
              alt="Scotty's Land Specialist Logo"
              className="h-20 sm:h-24 w-auto object-contain object-center drop-shadow-lg"
            />
          </div>
          <h2 className="font-display text-xl sm:text-2xl font-bold text-white uppercase leading-tight tracking-wide">
            Get A Free Quote
          </h2>
          <p className="text-[11px] text-white/70 mt-0.5">
            Tell us what you need and request a free quote.
          </p>
        </div>

        {isSuccess ? (
          <div className="text-center py-6 space-y-4">
            <CheckCircle2 className="w-14 h-14 text-[#D96B27] mx-auto" />
            <h3 className="text-lg font-bold font-display uppercase">
              Quote Request Sent!
            </h3>
            <p className="text-xs text-white/80">
              Thanks for reaching out. Someone on the team will get back to you about your
              project and free quote.
            </p>
            <div className="flex justify-center gap-3 pt-2">
              <button
                onClick={() => setIsSuccess(false)}
                className="text-xs underline text-[#D96B27] hover:text-white font-semibold"
              >
                Send another request
              </button>
              <button
                onClick={onClose}
                className="bg-[#D96B27] text-white px-5 py-2 rounded-full font-bold text-xs hover:bg-[#BA541B] transition-all"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label
                htmlFor={`${fieldId}-name`}
                className="block text-xs font-semibold text-white/80 mb-1"
              >
                Full Name *
              </label>
              <input
                id={`${fieldId}-name`}
                name="fullName"
                type="text"
                placeholder="Full Name"
                value={formData.name}
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                className="w-full bg-[#F8F6F1] text-[#1a1a1a] placeholder:text-gray-500 rounded-lg px-3.5 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#D96B27]"
                required
              />
            </div>

            <div>
              <label
                htmlFor={`${fieldId}-phone`}
                className="block text-xs font-semibold text-white/80 mb-1"
              >
                Phone *
              </label>
              <input
                id={`${fieldId}-phone`}
                name="phone"
                type="tel"
                placeholder="(801) 555-0123"
                value={formData.phone}
                onChange={(e) =>
                  setFormData({ ...formData, phone: e.target.value })
                }
                className="w-full bg-[#F8F6F1] text-[#1a1a1a] placeholder:text-gray-500 rounded-lg px-3.5 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#D96B27]"
                required
              />
            </div>

            <div>
              <label
                htmlFor={`${fieldId}-email`}
                className="block text-xs font-semibold text-white/80 mb-1"
              >
                Email Address
              </label>
              <input
                id={`${fieldId}-email`}
                name="email"
                type="email"
                placeholder="you@email.com"
                value={formData.email}
                onChange={(e) =>
                  setFormData({ ...formData, email: e.target.value })
                }
                className="w-full bg-[#F8F6F1] text-[#1a1a1a] placeholder:text-gray-500 rounded-lg px-3.5 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#D96B27]"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label
                  htmlFor={`${fieldId}-service`}
                  className="block text-xs font-semibold text-white/80 mb-1"
                >
                  Service Needed *
                </label>
                <select
                  id={`${fieldId}-service`}
                  name="service"
                  value={formData.service}
                  onChange={(e) =>
                    setFormData({ ...formData, service: e.target.value })
                  }
                  className="w-full bg-[#F8F6F1] text-[#1a1a1a] rounded-lg px-3.5 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#D96B27] cursor-pointer"
                  required
                >
                  <option value="" disabled>
                    -- Select a Service --
                  </option>
                  {quoteServiceOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label
                  htmlFor={`${fieldId}-location`}
                  className="block text-xs font-semibold text-white/80 mb-1"
                >
                  City / Location
                </label>
                <input
                  id={`${fieldId}-location`}
                  name="location"
                  type="text"
                  placeholder="Highland, UT"
                  value={formData.location}
                  onChange={(e) =>
                    setFormData({ ...formData, location: e.target.value })
                  }
                  className="w-full bg-[#F8F6F1] text-[#1a1a1a] placeholder:text-gray-500 rounded-lg px-3.5 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#D96B27]"
                />
              </div>
            </div>

            <div>
              <label
                htmlFor={`${fieldId}-details`}
                className="block text-xs font-semibold text-white/80 mb-1"
              >
                Project Details (optional)
              </label>
              <textarea
                id={`${fieldId}-details`}
                name="details"
                placeholder="Tell us a little about the job..."
                value={formData.details}
                onChange={(e) =>
                  setFormData({ ...formData, details: e.target.value })
                }
                rows={2}
                className="w-full bg-[#F8F6F1] text-[#1a1a1a] placeholder:text-gray-500 rounded-lg px-3.5 py-2.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#D96B27] resize-none"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-[#D96B27] border-2 border-[#D96B27] hover:bg-[#BA541B] text-white font-bold text-sm py-3 px-6 rounded-full transition-all duration-200 mt-2 shadow-lg disabled:opacity-50 hover:scale-[1.01] uppercase tracking-wide"
            >
              {isSubmitting ? "Submitting..." : "Get My Free Quote"}
            </button>
            <p className="text-[10px] text-white/50 text-center">
              Or call the team at{" "}
              <a
                href={PHONE_LINK}
                className="text-[#D96B27] font-semibold underline"
              >
                {PHONE_DISPLAY}
              </a>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

interface CallAndQuoteButtonsProps {
  className?: string;
  size?: "sm" | "md" | "lg";
  darkBg?: boolean;
  defaultService?: string;
  nowrap?: boolean;
}

export const CallAndQuoteButtons: React.FC<CallAndQuoteButtonsProps> = ({
  className = "",
  size = "md",
  darkBg = true,
  defaultService = "",
  nowrap = false,
}) => {
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  const sizeStyles = {
    sm: {
      btn: "px-3.5 py-2 text-xs",
      icon: "w-3.5 h-3.5",
      gap: "gap-1.5",
      tag: "text-[9px]",
    },
    md: {
      btn: "px-5 py-3 text-xs sm:text-sm",
      icon: "w-4 h-4",
      gap: "gap-2",
      tag: "text-[10px]",
    },
    lg: {
      btn: "px-7 py-3.5 sm:px-8 sm:py-4 text-sm sm:text-base",
      icon: "w-4 h-4 sm:w-5 sm:h-5",
      gap: "gap-2.5",
      tag: "text-xs",
    },
  }[size];

  return (
    <>
      <div className={`flex flex-col items-center ${className}`}>
        <div
          className={`flex items-start gap-3 ${nowrap ? "flex-nowrap" : "flex-wrap justify-center"}`}
        >
          <div className="flex flex-col items-center">
            <a
              href={PHONE_LINK}
              className={`bg-[#D96B27] border-2 border-[#D96B27] text-white hover:bg-[#BA541B] hover:border-[#BA541B] font-bold rounded-full flex items-center justify-center ${sizeStyles.gap} ${sizeStyles.btn} transition-all shadow-md hover:scale-[1.02] uppercase tracking-wide`}
            >
              <Phone className={sizeStyles.icon} />
              <span>{PHONE_DISPLAY}</span>
            </a>
            <span
              className={`${sizeStyles.tag} font-bold uppercase tracking-[0.15em] mt-1 text-center ${
                darkBg ? "text-white/80" : "text-[#252A2B]/80"
              }`}
            >
              We text 24/7
            </span>
          </div>

          <div className="flex flex-col items-center">
            <button
              onClick={() => setIsQuoteModalOpen(true)}
              className={`bg-white border-2 border-[#252A2B] text-[#252A2B] hover:bg-[#252A2B] hover:text-white font-bold rounded-full flex items-center justify-center ${sizeStyles.gap} ${sizeStyles.btn} transition-all shadow-md hover:scale-[1.02] uppercase tracking-wide`}
            >
              <span>Get Free Quote</span>
            </button>
            <span
              className={`${sizeStyles.tag} font-bold uppercase tracking-[0.15em] mt-1 text-center invisible`}
            >
              We text 24/7
            </span>
          </div>
        </div>
      </div>

      <QuoteModal
        isOpen={isQuoteModalOpen}
        onClose={() => setIsQuoteModalOpen(false)}
        defaultService={defaultService}
      />
    </>
  );
};
